import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { updateAlternatePlan, deleteAlternatePlan } from "../../lib/api";
import AltPlanFields from "../students/AltPlanFields";

const EMPTY = { plan_type: "weekly", days: [], dates: [], start_date: "", end_date: "", arrival_time: "", departure_time: "", destination: "", notes: "", full_day_out: false };

/**
 * Inline editor for an alternate plan, opened from the Master Schedule "Alt Plans" row.
 * Lets staff Edit the recurring plan (weekly OR specific dates), Skip just the shown week,
 * or Remove it completely.
 */
export default function AltPlanEditor({ plan, open, onClose, blocks = [], weekMonday, weekLabel, onChanged }) {
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (plan) {
      setForm({
        plan_type: ["dates", "range"].includes(plan.plan_type) ? plan.plan_type : "weekly",
        days: plan.days || [],
        dates: plan.dates || [],
        start_date: plan.start_date || "",
        end_date: plan.end_date || "",
        arrival_time: plan.arrival_time || "",
        departure_time: plan.departure_time || plan.effective_departure || "",
        destination: plan.destination || "",
        notes: plan.notes || "",
        full_day_out: !!plan.full_day_out,
      });
    }
  }, [plan]);

  if (!plan) return null;

  const skipped = (plan.skip_dates || []).includes(weekMonday);

  const handleSave = async () => {
    const isDates = form.plan_type === "dates";
    const isRange = form.plan_type === "range";
    const hasWindow = form.arrival_time || form.departure_time;
    const fullOut = !!form.full_day_out;
    const scheduleMissing = isRange ? (!form.start_date || !form.end_date) : isDates ? !form.dates.length : !form.days.length;
    if (scheduleMissing || (!fullOut && !hasWindow) || !form.destination) {
      toast.error(isRange ? "A start date, end date, destination, and an arrival/departure time (unless full day out) are required" : isDates ? "Dates, a destination, and an arrival/departure time (unless full day out) are required" : "Days, a destination, and an arrival/departure time (unless full day out) are required");
      return;
    }
    if (isRange && form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error("End date must be on or after the start date");
      return;
    }
    if (!fullOut && form.arrival_time && form.departure_time && form.departure_time <= form.arrival_time) {
      toast.error("Departure time must be after arrival time");
      return;
    }
    setSaving(true);
    try {
      await updateAlternatePlan(plan.id, form);
      toast.success("Alternate plan updated");
      onChanged && (await onChanged());
      onClose();
    } catch {
      toast.error("Failed to update plan");
    } finally {
      setSaving(false);
    }
  };

  const handleSkipWeek = async () => {
    setSaving(true);
    try {
      const current = plan.skip_dates || [];
      const next = skipped ? current.filter((d) => d !== weekMonday) : [...current, weekMonday];
      await updateAlternatePlan(plan.id, { skip_dates: next });
      toast.success(skipped ? "Restored for this week" : "Skipped for this week");
      onChanged && (await onChanged());
      onClose();
    } catch {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Remove "${plan.destination}" completely from ${plan.short_name || "this student"}'s schedule? This deletes the plan for all dates.`)) return;
    setSaving(true);
    try {
      await deleteAlternatePlan(plan.id);
      toast.success("Alternate plan removed");
      onChanged && (await onChanged());
      onClose();
    } catch {
      toast.error("Failed to remove");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="alt-plan-editor">
        <DialogHeader>
          <DialogTitle>{plan.student_name || plan.short_name} — Alternate Plan</DialogTitle>
        </DialogHeader>
        <div className="pt-1">
          <AltPlanFields form={form} setForm={setForm} blocks={blocks} testidPrefix="alt-edit-" />

          <div className="rounded-lg border bg-amber-50/60 p-3 space-y-2 mt-4">
            <div className="text-xs font-medium text-amber-800">Just this week ({weekLabel})</div>
            <Button variant="outline" size="sm" className="w-full border-amber-300 text-amber-800 hover:bg-amber-100" onClick={handleSkipWeek} disabled={saving} data-testid="alt-skip-week-btn">
              {skipped ? "Restore for this week" : "Skip just this week"}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t">
          <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={handleDelete} disabled={saving} data-testid="alt-remove-completely-btn">Remove completely</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={handleSave} disabled={saving} data-testid="alt-save-btn">{saving ? "Saving..." : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
